/* Smartap chrome: keyboard behaviour Horizon's mobile drawer (assets/header-drawer.js) leaves out,
   added from outside so the stock file stays unedited. Found by the design QA panel on 2026-09-26,
   measured in a headless browser at 375:
   1. Closing the drawer (Escape or the close button) left focus on <body> (WCAG 2.4.3).
   2. With the drawer open, focus stayed on the menu toggle under the panel, and the toggle stayed in
      the tab loop (WCAG 2.4.11).
   3. Escape from inside an open accordion (Products, Designs) closed nothing, because Horizon closes
      the nearest <details>, which is the accordion, not the drawer.
   Focus moves with setTimeout, not requestAnimationFrame: hidden tabs do not run animation frames
   (.claude/qa-project.md, gotchas). */

(() => {
  const DRAWER = 'header-drawer';

  function init(drawer) {
    const details = drawer.querySelector('details.menu-drawer-container');
    const toggle = details?.querySelector(':scope > summary');
    if (!details || !toggle || drawer.dataset.stChrome) return;
    drawer.dataset.stChrome = 'true';

    const closeButton = () => drawer.querySelector('.menu-drawer > .menu-drawer__close-button');
    const visible = (el) =>
      el.getClientRects().length > 0 &&
      (el.checkVisibility?.({ contentVisibilityAuto: true, opacityProperty: true, visibilityProperty: true }) ?? true);
    const focusables = () =>
      [...drawer.querySelectorAll('.menu-drawer a[href], .menu-drawer button:enabled, .menu-drawer summary')].filter(visible);

    // Horizon's trap (assets/focus.js) counts the toggle as its first stop, so Tab from the last
    // item and Shift+Tab from the close button both land on it. While open, send it on: forward to
    // the close button, backward to the last item.
    toggle.addEventListener('focus', (event) => {
      if (!details.open) return;
      if (event.relatedTarget !== closeButton()) {
        closeButton()?.focus();
        return;
      }
      // Backward: the last item that will actually take focus (a closed panel's controls will not).
      for (const el of focusables().reverse()) {
        el.focus();
        if (document.activeElement === el) return;
      }
    });

    details.addEventListener('toggle', () => {
      if (details.open) {
        // The panel only becomes visible once Horizon adds .menu-open, so wait for the close button.
        let tries = 0;
        const moveIn = () => {
          const button = closeButton();
          if (button && visible(button)) {
            button.focus();
          } else if (tries++ < 40) {
            setTimeout(moveIn, 25);
          }
        };
        setTimeout(moveIn, 0);
        return;
      }
      const lost = document.activeElement === document.body || details.contains(document.activeElement);
      if (lost) setTimeout(() => toggle.focus(), 0);
    });

    drawer.addEventListener('keyup', (event) => {
      if (event.key !== 'Escape' || !details.open) return;
      const inner = event.target instanceof Element ? event.target.closest('details') : null;
      if (inner && inner !== details && typeof drawer.close === 'function') drawer.close();
    });
  }

  function run() {
    document.querySelectorAll(DRAWER).forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
})();

/* Cart drawer overlay (assets/st-chrome.css): the dimmed layer is the page wrapper's own ::after, so a
   click on it targets the wrapper itself; that closes the drawer through its own close button, so
   Horizon's focus return and state stay as they are. */
document.addEventListener('click', (event) => {
  const wrapper = document.querySelector('.page-wrapper--drawer-open');
  if (!wrapper || event.target !== wrapper || !window.matchMedia('(min-width: 750px)').matches) return;
  const open = [...document.querySelectorAll('dialog[open]')].find((d) => d.querySelector('cart-drawer-component'));
  open?.querySelector('.theme-drawer__close-button')?.click();
});

/* Hidden slides out of the tab order (axe aria-hidden-focus, 2026-10-09). Horizon's slideshow marks
   slides it is not showing aria-hidden but leaves what is inside them focusable (a gallery video's
   controls). A slide it hides is made inert as well, and only while the slide lies outside its
   scroller, so a slide on screen is never made unclickable; it is released as soon as Horizon shows
   it again. Horizon's own code is unchanged. */
(() => {
  const offscreen = (slide) => {
    const scroller = slide.closest('slideshow-slides') || slide.parentElement;
    if (!scroller) return true;
    const a = slide.getBoundingClientRect();
    const b = scroller.getBoundingClientRect();
    if (a.width === 0 || a.height === 0) return true;
    return a.right <= b.left + 1 || a.left >= b.right - 1 || a.bottom <= b.top + 1 || a.top >= b.bottom - 1;
  };
  const sync = (slide) => {
    const hide = slide.getAttribute('aria-hidden') === 'true' && offscreen(slide);
    if (slide.inert !== hide) slide.inert = hide;
  };
  const all = () => document.querySelectorAll('slideshow-slide[aria-hidden]').forEach(sync);
  new MutationObserver((records) => {
    for (const record of records) {
      if (record.target instanceof Element && record.target.matches('slideshow-slide')) sync(record.target);
    }
  }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', all);
  else all();
  window.addEventListener('load', all);
})();

/* The announcement carousel (2026-10-09: delivery and returns as two messages that rotate). Horizon
   suspends its autoplay while a pointer is over it; this does the same while keyboard focus is
   inside (its arrows), and stops it for good under reduced motion, where the arrows still step
   through the messages. */
(() => {
  const BAR = 'announcement-bar-component';
  const wire = (bar) => {
    if (bar.dataset.stWired) return;
    bar.dataset.stWired = '1';
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches && typeof bar.pause === 'function') {
      bar.pause();
      return;
    }
    bar.addEventListener('focusin', () => typeof bar.suspend === 'function' && bar.suspend());
    bar.addEventListener('focusout', (e) => {
      if (!bar.contains(e.relatedTarget) && typeof bar.resume === 'function') bar.resume();
    });
  };
  const all = () => customElements.whenDefined(BAR).then(() => document.querySelectorAll(BAR).forEach(wire));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', all);
  else all();
  document.addEventListener('shopify:section:load', all);
})();

/* Jumps to an anchor on a desktop (2026-10-12, Paulius: the quote page "isn't loading if you click
   the get a quote from mega menu"). From 990px Horizon scrolls the page inside .page-wrapper and
   gives the root overflow: hidden (assets/base.css). A link to an anchor (/pages/contact?view=bulk
   #quote) still scrolls the root, which a hidden overflow does not prevent, so the whole page slid up
   575px under a blank band and nothing could scroll it back. Whenever the root has scrolled while it
   is not meant to, it is put back and the anchor is scrolled into view inside .page-wrapper instead
   (or the distance handed to it when there is no anchor), so the page is whole and the jump still
   lands. Phones scroll the window, so nothing changes there. */
(() => {
  const root = document.scrollingElement || document.documentElement;
  let busy = false;
  // Instant moves: Horizon sets scroll-behavior: smooth on <html>, under which these would animate
  // and each frame of the animation would call this again.
  const handBack = () => {
    const lost = root.scrollTop;
    if (!lost || busy) return;
    const wrapper = document.querySelector('.page-wrapper');
    if (!wrapper || getComputedStyle(document.documentElement).overflowY !== 'hidden') return;
    busy = true;
    root.scrollTo({ top: 0, behavior: 'instant' });
    // Then make the jump the link asked for, inside the page's own scroller (scroll-margin and all).
    const id = decodeURIComponent(location.hash.slice(1));
    const target = id && document.getElementById(id);
    if (target) target.scrollIntoView({ block: 'start', behavior: 'instant' });
    else wrapper.scrollBy({ top: lost, behavior: 'instant' });
    if (root.scrollTop) root.scrollTo({ top: 0, behavior: 'instant' });
    busy = false;
  };
  window.addEventListener('scroll', handBack, { passive: true });
  window.addEventListener('hashchange', handBack);
  window.addEventListener('load', handBack);
  if (document.readyState !== 'loading') handBack();
  else document.addEventListener('DOMContentLoaded', handBack);
})();
